/* =========================================================================
   estate-callback.ts — inbound estate-SSO consumer.

   Sibling estate apps hand their Supabase session to this app via
   `/auth/callback#access_token=...&refresh_token=...`. This app has no URL
   router, so the consumer runs once at boot instead of on a route:

   1. The hash is captured SYNCHRONOUSLY at module evaluation, before
      supabase-js's async detectSessionInUrl task can consume and strip it.
   2. The tokens (and the whole /auth/callback path) are stripped from
      history immediately — they must never survive in the URL.
   3. `consumeEstateHandoff()` hands the captured tokens to
      supabase.auth.setSession; AuthProvider picks the session up through
      onAuthStateChange.

   The `next` param of the handoff contract is accepted but unused — this
   app has no in-app URL paths to deep-link to.
   ========================================================================= */

import { supabase, isSupabaseConfigured } from "./supabase-client";

// Synchronous capture — runs at import time, ahead of any async consumer.
const capturedHash =
  typeof globalThis.location !== "undefined" &&
  globalThis.location.pathname === "/auth/callback"
    ? globalThis.location.hash.replace(/^#/, "")
    : null;

if (capturedHash !== null) {
  // Strip tokens + dead path from the URL before anything renders.
  globalThis.history.replaceState(null, "", "/");
}

/** Consume a captured estate handoff, if one arrived. Safe to call always. */
export async function consumeEstateHandoff(): Promise<void> {
  if (capturedHash === null || !isSupabaseConfigured()) return;

  const params = new URLSearchParams(capturedHash);
  const access_token = params.get("access_token");
  const refresh_token = params.get("refresh_token");
  if (!access_token || !refresh_token) return;

  const { error } = await supabase.auth.setSession({
    access_token,
    refresh_token,
  });
  if (error) {
    console.warn(
      "[estate-sso] inbound handoff setSession failed:",
      error.message,
    );
    return;
  }
}

/** Test-only visibility into the synchronous capture. */
export const _testing = { capturedHash };
